/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-shadow */

import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import * as React from 'react';
import _ from 'lodash';

// material-ui
import {
    DataGridPro,
    GridColDef,
    useGridApiRef,
    GridRowParams,
    GridAlignment,
    GridRowId,
    gridFilterModelSelector,
    GridPreferencePanelParams,
    GridPreferencePanelsValue,
    GridSortModel,
    gridSortModelSelector,
    GridLogicOperator,
    GridSortDirection,
    GridState,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridCellModesModel
} from '@mui/x-data-grid-pro';
import useMyMenuItems from 'hooks/useMyMenuItems';
import { Box, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PushPin as PushPinIcon } from '@mui/icons-material';

// graphql
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { QUERY_FIND_RECORD_HEADERS_WITH_FILTERING, QUERY_FIND_RECORD_TYPE, QUERY_GET_RECORD_FIELDS } from 'graphql/queries/bills';
import { MUTATION_UPDATE_RECORD_HEADER, MUTATION_BULK_UPDATE_RECORD_HEADER } from 'graphql/mutations/bills';

import { useSelector, useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

import { capitalize } from 'utils/stringHelpers';

import {
    CustomGridFilterModel,
    FilterRecordHeaderInput,
    FindRecordHeaderWithFilteringVariables,
    GetZipFileSignedURL,
    IFindRecordTypeSummarize,
    IGetRecordFields,
    IGetRecordHeadersFiltered,
    IGridPreference,
    IObjectValues,
    IRecordFieldsList,
    IRecordHeaders,
    ObjectPanelMode
} from '../types';
import { CustomColumnMenu, CustomColumnMenuProps, FilterPanel, FilterPanelProps, UpdateRowsBanner } from './components';
import { RecordGridActionsColumn } from './components/RecordGridActionsColumn';
import SkeletonGrid from './SkeletonGrid';
import {
    getAditionalFields,
    editableFields,
    getEditableField,
    filterNotAllowedFields,
    getRenderEditCell,
    getValueSetter,
    getValueGetter,
    getRenderCell,
    handleRowEditStop,
    getDensityFactor,
    getValueFormatter,
    generateGridState,
    findCellsEdited,
    getRecordViewerURL,
    getDataFocusViewURL
} from '../utils';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { IGetRecordFileById, IUpdateBulkRecordHeader, IUpdateRecordHeader, RecordFileByIdVariables } from 'ui-component/RecordView/types';
import useMyRole from 'hooks/useMyRole';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import CustomDetailPanel from '../CustomDetailPanel';
import CloseDetailsPanelDialog from '../CustomDetailPanel/CloseDetailsPanelDialog';
import { IFindAllLineItemHeaders } from '../CustomDetailPanel/types';
import { QUERY_FIND_ALL_LINE_ITEMS_HEADERS } from 'graphql/queries/lineItems';
import { QUERY_GET_MY_RECORDS_FILE_BY_ID } from 'graphql/queries/records';
import { downloadPdf, getDataToUpdate } from 'ui-component/RecordView/utils';
import {
    NON_EDITABLE_DATA_TYPES,
    NON_FILTERABLE_OR_SORTABLE_FIELDS,
    NON_SORTABLE_DATA_TYPES,
    checkFilterValue,
    generateFilterPayloads,
    getFilterOperator
} from '../utils/filterHelpers';
import { getAditionalFieldsHeaders, getBaseFields } from 'ui-component/RecordView/PropertiesPanel/utils';
import { CustomToolbar, ICustomToolbar } from 'ui-component/gridToolbar/CustomToolbar';
import { checkIsEditing, generateEditModel, generateUpdatedList, generateViewModel, handleCellKeydown } from '../utils/editHelpers';
import { PaginationFooter, PaginationFooterProps } from './components/PaginationFooter';
import { getRowCustomClassName } from '../utils/rowHelpers';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { GoogleAnalyticsEventHandler } from 'utils/analytics';
import BulkEditDialog from './components/BulkEditDialog';
import { GridOptions } from '../GridOptions';
import { FindObjectProperty, FindObjectPropertyVariables, ObjectProperty } from 'views/backoffice/CustomObjects/types';
import ObjectPanelEditForm from './components/ObjectPanelEditForm';
import { QUERY_GET_OBJECT_PROPERTIES } from 'graphql/queries/customObjects';
import ObjectPanelView from './components/ObjectPanelView';
import { adjustHeaders, getTogglableColumns } from '../utils/headerHelpers';
import { useQueryParams } from 'hooks/useQueryParams';
import { CustomCheckboxSelectionHeader } from './components/CustomCheckboxSelectionHeader';
import { QUERY_GET_ZIP_FILE_SIGNED_URL } from 'graphql/queries/bulkActions';
import { useRecordData } from 'ui-component/RecordView/PropertiesPanel/hooks';
import { getDocumentFileUrl } from 'utils/S3Uploader';
import PanelTitleWithShortCut from 'ui-component/RecordView/RecordViewCore/components/PanelTitleWithShortCut';
import { activeAction, cleanOpenedAction } from 'store/slices/menu';
import RightHandResizerContainer from 'ui-component/RightHandResizerContainer';
import { PinnedActionsProvider } from './components/PinnedActionsContext';
import { generateStateToRestore } from '../utils/stateHelpers';
import { useSelectedAndDefaultListTypesValues } from 'hooks/useSelectedAndDefaultListTypesValues';
import useIsAllowed from 'hooks/useIsAllowed';
import { IRecordType } from 'types/recordType';
import { getRecordTypeSlug } from 'utils/recordTypeSlug';

const useStyles = makeStyles((theme) => ({
    gridOptionsButton: {
        padding: '4px',
        paddingLeft: '0',
        '&:hover': { borderRadius: '0 !important' }
    },
    evenRow: {
        '& div button, div a': { color: theme.palette.primary[600] },
        '&:hover svg[data-testid="ExpandMoreIcon"]': {
            fill: 'white'
        },
        '&:hover, &.MuiDataGrid-row.Mui-hovered': {
            backgroundColor: `${theme.palette.primary.dark} !important`,
            color: `${theme.palette.background.paper} !important`,
            '& div button, div a, .gridObjectValue:not(.non-editable-cell)': { color: theme.palette.background.paper },
            '& .non-editable-cell': {
                color: `${theme.palette.primary[600]} !important`,
                '& .gridObjectValue': {
                    color: `${theme.palette.primary.dark} !important`
                }
            },
            '& div button.MuiIconButton-root': { color: theme.palette.primary[600] },
            '& .MuiAutocomplete-popupIndicator, .MuiAutocomplete-popupIndicator > svg, .MuiAutocomplete-clearIndicator, .MuiAutocomplete-clearIndicator > svg':
                {
                    backgroundColor: 'transparent !important',
                    fill: `${theme.palette.primary.dark} !important`
                },
            '& svg[data-testid="CheckBoxIcon"], svg[data-testid="CheckBoxOutlineBlankIcon"], svg[data-testid="VisibilityIcon"], svg[data-testid="FileDownloadIcon"], svg[data-testid="ShareIcon"], svg[data-testid="AddIcon"], svg.custom-icon':
                {
                    color: `${theme.palette.background.paper} !important`,
                    backgroundColor: 'transparent !important'
                },
            '& .MuiDataGrid-cell--editing, .MuiDataGrid-cell--editing>div>div>div>div>div, .MuiDataGrid-cell--editing>div>div>div>div>div>div>div, .MuiDataGrid-cell--editing > .MuiBox-root > .MuiFormControl-root > .MuiInputBase-root, .MuiDataGrid-cell--editing input, div[aria-haspopup="listbox"]':
                {
                    backgroundColor: `${theme.palette.secondary.light}`,
                    color: `${theme.palette.primary[600]} !important`,
                    '& svg[data-testid="CheckBoxIcon"], svg[data-testid="CheckBoxOutlineBlankIcon"], svg[data-testid="ExpandMoreIcon"]': {
                        color: `${theme.palette.primary.dark} !important`,
                        fill: `${theme.palette.primary.dark} !important`
                    }
                }
        },
        '&:hover svg[data-testid="ExpandMoreIcon"], &:hover path[data-testid="ExpandMoreIcon"]': {
            fill: 'white'
        }
    },
    oddRow: {
        backgroundColor: `${theme.palette.primary[200]} !important`,
        '& div button, div a': { color: theme.palette.primary[600] },
        '&:hover svg[data-testid="ExpandMoreIcon"]': {
            fill: 'white'
        },
        '&:hover, &.MuiDataGrid-row.Mui-hovered': {
            backgroundColor: `${theme.palette.primary.dark} !important`,
            color: `${theme.palette.background.paper} !important`,
            '& div button, div a, .gridObjectValue:not(.non-editable-cell)': { color: theme.palette.background.paper },
            '& .non-editable-cell': {
                color: `${theme.palette.primary[600]} !important`,
                '& .gridObjectValue': {
                    color: `${theme.palette.primary.dark} !important`
                }
            },
            '& div button.MuiIconButton-root': { color: theme.palette.primary[600] },
            '& .MuiAutocomplete-popupIndicator, .MuiAutocomplete-popupIndicator > svg, .MuiAutocomplete-clearIndicator, .MuiAutocomplete-clearIndicator > svg':
                {
                    backgroundColor: 'transparent !important',
                    fill: `${theme.palette.primary.dark} !important`
                },
            '& svg[data-testid="CheckBoxIcon"], svg[data-testid="CheckBoxOutlineBlankIcon"], svg[data-testid="VisibilityIcon"], svg[data-testid="FileDownloadIcon"], svg[data-testid="ShareIcon"], svg[data-testid="AddIcon"], svg.custom-icon':
                {
                    color: `${theme.palette.background.paper} !important`,
                    backgroundColor: 'transparent !important'
                },
            '& .MuiDataGrid-cell--editing, .MuiDataGrid-cell--editing>div>div>div>div>div, .MuiDataGrid-cell--editing>div>div>div>div>div>div>div, .MuiDataGrid-cell--editing > .MuiBox-root > .MuiFormControl-root > .MuiInputBase-root, .MuiDataGrid-cell--editing input, div[aria-haspopup="listbox"]':
                {
                    backgroundColor: `${theme.palette.secondary.light}`,
                    color: `${theme.palette.primary[600]} !important`,
                    '& svg[data-testid="CheckBoxIcon"], svg[data-testid="CheckBoxOutlineBlankIcon"], svg[data-testid="ExpandMoreIcon"]': {
                        color: `${theme.palette.primary.dark} !important`,
                        fill: `${theme.palette.primary.dark} !important`
                    }
                }
        },
        '&:hover svg[data-testid="ExpandMoreIcon"], &:hover path[data-testid="ExpandMoreIcon"]': {
            fill: 'white'
        }
    },
    gridToolbarCustomFont: { fontSize: '0.8125rem', fontWeight: '500', color: '#858585', marginLeft: '4px' },
    gridToolbarCustomFontBlack: { fontSize: '0.8125rem', fontWeight: '500', color: '#000', marginLeft: '4px' },
    paginationItems: { display: 'flex', justifyContent: 'flex-end' },
    paginationArrow: { fontSize: '0.6875', fontWeight: '500', color: '#858585', marginLeft: '4px' },
    paginationControl: {
        '&:hover': {
            backgroundColor: 'transparent !important',
            borderRadius: '0 !important'
        },
        '&:disabled': {
            '& > svg': {
                color: '#C3C3C3 !important'
            }
        }
    },
    disabledRow: {
        opacity: 0.7
    },
    '@global': {
        '[data-id^="preset-"] .MuiDataGrid-filterFormDeleteIcon': {
            display: 'none !important',
            pointerEvents: 'none'
        }
    }
}));

export interface IRecordGrid {
    recordTypeId: number;
    gridName: string;
}

export const RecordGrid = ({ recordTypeId, gridName = 'bills' }: IRecordGrid) => {
    const classes = useStyles();
    const { getMenuPermissionsByRecordTypeId, loading: loadingMyMenuItems } = useMyMenuItems();
    const roleId = useMyRole();

    const storeDispatch = useDispatch();
    const apiRef = useGridApiRef();
    const intl = useIntl();
    const queryParams = useQueryParams();
    const { pathname: currentPathName } = useLocation();
    const navigate = useNavigate();
    const modal = useConfirmationModalContext();
    const { actionData, drawerOpen } = useSelector((store) => store.menu);
    const roleActions = getMenuPermissionsByRecordTypeId?.(recordTypeId, (roleId ?? 0) as number);
    const canViewRecordViewer = roleActions
        ?.filter((el) => el.name.toLowerCase().includes('record viewer access'))
        ?.some((el) => el.enabled);

    const canCreateNewRecord = roleActions?.filter((el) => el.name.toLowerCase().includes('create records'))?.some((el) => el.enabled);

    const { isAllowed: isAllowedDelete } = useIsAllowed('RECORD_SOFT_DELETE');
    const { isAllowed: isAllowedLineItemAccess } = useIsAllowed('LINE_ITEM_ACCESS');
    const { isAllowed: isAllowedDataFocusView } = useIsAllowed('DATA_FOCUS_VIEW');
    const { isAllowed: isAllowedDynamicDataFocusView } = useIsAllowed('DYNAMIC_DATA_VIEWS');
    const { isAllowed: isAllowedEditRecords } = useIsAllowed('ENRICH_RECORD_DATA');

    const IsSalesOrder = useMemo(() => {
        const SalesOrderId = actionData.find((el) => el.menuItem.name === 'Sales Order')?.menuItem.recordType?.id;

        return Number(SalesOrderId) === Number(recordTypeId);
    }, [actionData, recordTypeId]);

    const IsPurchaseOrder = useMemo(() => {
        const PurchaseOrderId = actionData.find((el) => el.menuItem.name === 'Purchase Order')?.menuItem.recordType?.id;

        return Number(PurchaseOrderId) === Number(recordTypeId);
    }, [actionData, recordTypeId]);

    const emptyFilterModel: CustomGridFilterModel = {
        items: [],
        logicOperator: 'and' as GridLogicOperator
    };

    const defaultFilterModelRef = useRef(emptyFilterModel);
    const viewFilterModelRef = useRef(emptyFilterModel);

    const hasLoadedInitialStateRef = useRef(false);
    const unsavedGridStateRef = useRef<GridInitialStatePro>();

    const [recordList, setRecordList] = useState<IRecordHeaders[]>([]);

    const [inputSearch, setInputSearch] = useState<string>('');
    const [showObjectPanel, setShowObjectPanel] = useState(false);
    // Super Record Type

    const [isSuperRecordType, setIsSuperRecordType] = useState(false);
    const [, setRelatedRecordTypes] = useState<number[]>([]);
    const [defaultCreation, setDefaultCreation] = useState<'RecordView' | 'DataFocusView' | null>(null);
    const [headerList, setHeaderList] = useState<IRecordFieldsList | undefined>(undefined);
    // Objects
    const [objectProperties, setObjectProperties] = useState<(ObjectProperty & { value: string; objectName: string })[]>([]);
    const [objectPanelMode, setObjectPanelMode] = useState(ObjectPanelMode.VIEW);
    const [selectedObjectToEdit, setSelectedObjectToEdit] = useState<{ id: number; field: string } | null>(null);
    const [isAllowedToEditObject, setIsAllowedToEditObject] = useState(true);
    const [isListObjectSelected, setIsListObjectSelected] = useState(true);

    // Grid Preferences states
    const [showResetRowOrder, setShowResetRowOrder] = useState(false);
    const [defaultGrid, setDefaultGrid] = useState<IGridPreference | null | undefined>(undefined);
    const [loadingPreferences, setLoadingPreferences] = useState(false);
    const densityFactorRef = useRef<number>(1);
    const [showSaveChanges, setShowSaveChanges] = useState(false);
    const [currentState, setCurrentState] = useState<GridInitialStatePro>();
    // const [columnsOrder, setColumnsOrder] = useState<string[]>([]);
    const [initialState, setInitialState] = useState<{
        state?: GridInitialStatePro;
        loading: boolean;
    }>({ loading: true });

    // Pagination and filtering states
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [showOnlyEnabledRecords, setShowOnlyEnabledRecords] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(25);
    const [rowCountState, setRowCountState] = useState(0);
    const [filters, setFilters] = useState<FilterRecordHeaderInput | undefined>();
    const [newFilterModel, setNewFilterModel] = useState<CustomGridFilterModel>();
    const [appliedFilterModel, setAppliedFilterModel] = useState<CustomGridFilterModel>();

    // Edit states
    const [cellModesModel, setCellModesModel] = useState<Record<string, any>>({});
    const [checkbox, setCheckbox] = useState<string[]>([]);

    // Bulk Edit States
    const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
    const [columnsEnabledToBulkEdit, setColumnsEnabledToBulkEdit] = useState<string[]>([]);

    const [isOpenImportDialog, setIsOpenImportDialog] = useState(false);
    const [objectsToCreate, setObjectsToCreate] = useState<Record<string, any[]>>({});

    // Line items states
    const [showCloseDetailsPanel, setShowCloseDetailsPanel] = useState(false);
    const [isEditingLineItem, setIsEditingLineItem] = useState(false);
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = React.useState<GridRowId[]>([]);
    const [newRowId, setNewRowId] = useState<number | null>(null);
    const [isBulkSelectionAllActive, setIsBulkSelectionAllActive] = useState(false);

    const [rightPanelAndDrawerWidth, setRightPanelAndDrawerWidth] = useState(0);
    const [rightPanelWidth, setRightPanelWidth] = useState(0);

    const isSameFilterModel = useMemo(
        () => _.isEqual(_.omit(newFilterModel, ['id']), _.omit(appliedFilterModel, ['id'])),
        [newFilterModel, appliedFilterModel]
    );

    const [getRecordFields, { loading: headerLoading }] = useLazyQuery<IGetRecordFields>(QUERY_GET_RECORD_FIELDS, {
        onError(error) {
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Failed to fetch grid preferences',
                    subtitle: error.message.replaceAll(':', ',')
                })
            );
        }
    });
    const [getZipFileUrl] = useLazyQuery<GetZipFileSignedURL>(QUERY_GET_ZIP_FILE_SIGNED_URL);

    const { loading: loadingRecordTypes } = useQuery<IFindRecordTypeSummarize>(QUERY_FIND_RECORD_TYPE, {
        variables: { data: { id: recordTypeId || 3 } },
        fetchPolicy: 'no-cache',
        onCompleted: async (data) => {
            if (data?.findRecordType) {
                setIsSuperRecordType(data.findRecordType[0].isSuper);
                setDefaultCreation(data.findRecordType[0].defaultCreation || null);
                const recordTypesIds = [recordTypeId];
                if (data.findRecordType[0].isSuper) {
                    data.findRecordType[0].relatedRecordTypes.forEach((recordType) => {
                        recordTypesIds.push(+recordType.id);
                    });
                }
                let headerListRaw: IRecordFieldsList | undefined;
                for await (const id of recordTypesIds) {
                    const { data } = await getRecordFields({ variables: { recordType: id } });
                    if (data?.getSaasRecordFieldsByRecordTypes) {
                        const newHeaders = { ...data.getSaasRecordFieldsByRecordTypes };
                        Object.keys(newHeaders).forEach((key) => {
                            newHeaders[key] = { ...newHeaders[key], recordTypeId: id };
                        });
                        headerListRaw = { ...headerListRaw, ...newHeaders };
                    }
                }
                setRelatedRecordTypes(recordTypesIds);
                setHeaderList(headerListRaw);
            }
        }
    });

    const {
        fieldAccess: fieldAccessList,
        errorFieldAccess: accessError,
        loadingFieldAccess: accessLoading,
        headers: fieldAccessHeaders
    } = useRecordData({
        recordTypeId
    });

    const [getRecords, { loading: listLoading, fetchMore }] = useLazyQuery<
        IGetRecordHeadersFiltered,
        FindRecordHeaderWithFilteringVariables
    >(QUERY_FIND_RECORD_HEADERS_WITH_FILTERING, {
        fetchPolicy: 'no-cache',
        onCompleted(data) {
            setRecordList(data.filterAndSortRecordHeader.records);
            if (!isSameFilterModel) setAppliedFilterModel(newFilterModel);
        },
        onError(error) {
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Failed to fetch record fields',
                    message: error.message.replaceAll(':', ',')
                })
            );
        }
    });

    const [getObjectProperties, { loading: loadingObjectProperties }] = useLazyQuery<FindObjectProperty, FindObjectPropertyVariables>(
        QUERY_GET_OBJECT_PROPERTIES
    );

    const [getRecordFile] = useLazyQuery<IGetRecordFileById, RecordFileByIdVariables>(QUERY_GET_MY_RECORDS_FILE_BY_ID);
    const [updateRecordHeader, { loading: updateLoading }] = useMutation<IUpdateRecordHeader>(MUTATION_UPDATE_RECORD_HEADER, {
        update(cache, { data }) {
            const updatedRecord = data?.updateRecordHeader;

            const list = cache.readQuery<IGetRecordHeadersFiltered, FindRecordHeaderWithFilteringVariables>({
                query: QUERY_FIND_RECORD_HEADERS_WITH_FILTERING,
                variables: {
                    pagination: {
                        offset: currentPage * currentPageSize,
                        limit: currentPageSize
                    },
                    data: {
                        recordTypeId: recordTypeId || 3,
                        tenantId: Number(localStorage.getItem('tenant_id')),
                        enabled: showOnlyEnabledRecords,
                        filters
                    }
                }
            });

            if (!updatedRecord || !list?.filterAndSortRecordHeader) return;

            const newList = generateUpdatedList(list.filterAndSortRecordHeader.records, updatedRecord, showOnlyEnabledRecords);
            const newTotal = showOnlyEnabledRecords && !updatedRecord.enabled ? rowCountState - 1 : rowCountState;
            cache.writeQuery<IGetRecordHeadersFiltered, FindRecordHeaderWithFilteringVariables>({
                query: QUERY_FIND_RECORD_HEADERS_WITH_FILTERING,
                variables: {
                    pagination: {
                        offset: currentPage * currentPageSize,
                        limit: currentPageSize
                    },
                    data: {
                        recordTypeId: recordTypeId || 3,
                        tenantId: Number(localStorage.getItem('tenant_id')),
                        enabled: showOnlyEnabledRecords || undefined,
                        filters
                    }
                },
                data: {
                    filterAndSortRecordHeader: {
                        records: newList,
                        total: newTotal
                    }
                }
            });
        }
    });

    const [updateBulkRecordHeader] = useMutation<IUpdateBulkRecordHeader>(MUTATION_BULK_UPDATE_RECORD_HEADER, {});

    const onCompleted = useCallback(() => {
        if (currentState) {
            const stateToRestore = generateStateToRestore(apiRef, currentState);
            apiRef.current.restoreState(stateToRestore);
        }
    }, [apiRef, currentState]);

    const { isLoading: loadingSelectedCustomListValues, listValuesByListId } = useSelectedAndDefaultListTypesValues(
        headerList,
        recordList || undefined,
        onCompleted
    );

    useEffect(() => {
        const disablePresetFilter = (filter: Element) => {
            const inputWrappper = filter.querySelector('.MuiDataGrid-filterFormValueInput') as HTMLInputElement | null;
            const logicOperatorSelectWraper = filter.querySelector('.MuiDataGrid-filterFormLogicOperatorInput') as HTMLElement | null;
            const columnSelectWrapper = filter.querySelector('.MuiDataGrid-filterFormColumnInput') as HTMLElement | null;
            const operatorSelectWrapper = filter.querySelector('.MuiDataGrid-filterFormOperatorInput') as HTMLElement | null;

            [inputWrappper, logicOperatorSelectWraper, columnSelectWrapper, operatorSelectWrapper].forEach((el) => {
                if (el) {
                    Object.assign(el.style, {
                        pointerEvents: 'none',
                        cursor: 'not-allowed'
                    });
                    el.setAttribute('tabIndex', '-1');
                }
            });
        };

        const observer = new MutationObserver(() => {
            document.querySelectorAll('[data-id^="preset-"]').forEach(disablePresetFilter);
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        document.querySelectorAll('[data-id^="preset-"]').forEach(disablePresetFilter);

        return () => observer.disconnect();
    }, []);

    const handleClickObject = useCallback(
        async (
            field: string,
            rowId: number,
            objectValuesDefinition: IObjectValues[],
            isEditable: boolean,
            objectDefinitionId,
            isListObject = false
        ) => {
            setSelectedObjectToEdit({ id: +rowId, field });
            if (!objectDefinitionId && !headerList?.[field]) return;
            setShowObjectPanel(true);
            const { data: objectPropertiesData } = await getObjectProperties({
                variables: {
                    data: {
                        objectDefinitionIds: +(objectDefinitionId ?? headerList?.[field].objectDefinition.id)
                    }
                }
            });
            const mappedProperties = [...(objectPropertiesData?.findObjectProperty ?? [])]
                .filter((property) => property.enabled)
                .sort((a, b) => {
                    const orderA = a.order;
                    const orderB = b.order;
                    if (orderA < orderB) {
                        return -1;
                    }
                    if (orderA > orderB) {
                        return 1;
                    }
                    return 0;
                })
                .map((property) => ({
                    ...property,
                    value: objectValuesDefinition.find((item) => Number(item.objectProperty.id) === Number(property.id))?.value || '',
                    objectName: property.objectDefinition?.name || ''
                }));
            setObjectProperties(mappedProperties || []);
            setIsAllowedToEditObject(isEditable);
            setIsListObjectSelected(isListObject);
        },
        [headerList, getObjectProperties]
    );

    const handleEditObjectFormInteraction = async (newProperties: (ObjectProperty & { value: string })[], modalTitle: string) => {
        await modal.showConfirmation({
            title: modalTitle,
            content: ``,
            loadingText: 'Updating',
            forwardButtonText: 'Save changes',
            actionButtonText: 'Discard changes',
            onForward: async () => handleUpdateObject(newProperties),
            onAction: () => {
                setSelectedObjectToEdit(null);
                setShowObjectPanel(false);
                setObjectProperties([]);
                setObjectPanelMode(ObjectPanelMode.VIEW);
            },
            showCheckbox: true,
            actionType: `${modalTitle === 'Do you want to save this changes?' ? `save` : `discard`}_object_changes`,
            sectionTitle: 'record_grid',
            recordType: String(gridName.toLowerCase().replaceAll(' ', '_')),
            hideCloseBtn: true
        });
    };

    const handleUpdateObject = async (newProperties: (ObjectProperty & { value: string })[]) => {
        if (isOpenImportDialog) {
            setObjectsToCreate((prev) => ({ ...prev, [`${selectedObjectToEdit?.field}`]: newProperties }));
            setSelectedObjectToEdit(null);
            setShowObjectPanel(false);
            setObjectPanelMode(ObjectPanelMode.VIEW);
            setObjectProperties([]);
            return;
        }
        modal.toogleLoading(true);
        const record: { [key: string]: number | string } = {};
        record.id = selectedObjectToEdit?.id || '';
        record[`${selectedObjectToEdit?.field}`] = '';
        const baseFieldHeaders = getBaseFields(headerList || null, fieldAccessList);
        const aditionalFieldHeaders = getAditionalFieldsHeaders(headerList, fieldAccessList) || null;
        const selectedRecord = recordList.find((el) => Number(el.id) === Number(record.id)) as IRecordHeaders;
        const { additionalFields, baseData } = getDataToUpdate(
            record,
            baseFieldHeaders,
            aditionalFieldHeaders,
            selectedRecord?.additionalFields || [],
            fieldAccessList
        );

        const data: any = {
            ...baseData,
            id: Number(baseData.id)
        };

        data.additionalFields = additionalFields;
        data.additionalFields[0].objectValuesByProperty = newProperties
            // .filter((property) => property.dataType !== 'checkbox')
            .map((property) => ({
                propertyId: +property.id,
                value: property.value
            }));
        try {
            const res = await new Promise<{ success: boolean; message?: string }>((res) => {
                updateRecordHeader({ variables: { data } })
                    .then(({ data }) => {
                        setRecordList((prev) => prev.map((el) => (el.id === data?.updateRecordHeader.id ? data?.updateRecordHeader : el)));
                        res({ success: true });
                    })
                    .catch((error) => res({ success: false, message: error.message }));
            });
            if (res.success) {
                storeDispatch(
                    openSnackbar({
                        open: true,
                        type: 'success',
                        title: 'Success',
                        subtitle: `Changes saved!`
                    })
                );
            } else {
                storeDispatch(
                    openSnackbar({
                        open: true,
                        type: 'error',
                        title: 'Save changes failed',
                        subtitle: res.message || 'Internal server error.'
                    })
                );
            }
        } catch (error: any) {
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Save changes failed',
                    subtitle: error.message || 'Internal server error.'
                })
            );
        }
        modal.toogleLoading(false);
        setSelectedObjectToEdit(null);
        setShowObjectPanel(false);
        setObjectProperties([]);
        setObjectPanelMode(ObjectPanelMode.VIEW);
    };

    // Line Items
    const {
        data: lineItemsData,
        loading: loadingLineItems,
        error: lineItemsError
    } = useQuery<IFindAllLineItemHeaders>(QUERY_FIND_ALL_LINE_ITEMS_HEADERS);

    const lineItemObj = useMemo(
        () =>
            lineItemsData?.findAllLineItemsByType.find((data) => {
                const recordTypesArr = data.recordType.map((el) => Number(el.id));
                return recordTypesArr.includes(recordTypeId);
            }) || null,
        [lineItemsData, recordTypeId]
    );

    const handleViewRecord = useCallback(
        (
            recordId: string,
            recordType: Pick<IRecordType, 'id' | 'name'>,
            type: 'document' | 'recordDetails' | 'dynamicDataFocusView' = 'document'
        ) => {
            console.log(recordType);
            const tenantUrl = localStorage.getItem('tenantPath');
            if (!tenantUrl) return;
            if (type === 'document')
                window.open(
                    `${tenantUrl}/accounting/doc-viewer/${getRecordTypeSlug(recordType.name)}?recordId=${recordId}&recordType=${
                        recordType.id
                    }`,
                    '_blank'
                );
            else if (type === 'dynamicDataFocusView')
                window.open(
                    `${tenantUrl}/data-focus-view/${getRecordTypeSlug(recordType.name)}?recordId=${recordId}&recordType=${recordType.id}`,
                    '_blank'
                );
            else {
                const path = IsSalesOrder ? 'sales-details-viewer' : IsPurchaseOrder ? 'pucharse-details-viewer' : 'record-details-viewer';
                window.open(
                    `${tenantUrl}/${path}/${getRecordTypeSlug(recordType.name)}?recordId=${recordId}&recordType=${recordType.id}`,
                    '_blank'
                );
            }
        },
        [IsPurchaseOrder, IsSalesOrder]
    );

    const handleRecordShareURL = useCallback(
        (recordId: string, recordType: Pick<IRecordType, 'id' | 'name'>) => {
            const url = getRecordViewerURL(recordId, recordType);
            if (!url) return;
            navigator.clipboard.writeText(url);
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: 'Success',
                    subtitle: 'Record link copied to clipboard'
                })
            );
        },
        [storeDispatch]
    );

    const handleDownloadFile = useCallback(
        async (fileId: string) => {
            try {
                const { data } = await getRecordFile({
                    variables: {
                        recordFileId: Number(fileId)
                    }
                });
                const recordFileUrl = data?.getMyRecordFileById ? getDocumentFileUrl(data.getMyRecordFileById) : null;
                if (recordFileUrl) downloadPdf(recordFileUrl, data?.getMyRecordFileById.originalFileName);
                else throw new Error('File not found');
            } catch (err: any) {
                console.log(err);
                storeDispatch(
                    openSnackbar({
                        open: true,
                        type: 'error',
                        title: 'Download failed',
                        subtitle: err.message || 'Internal server error.'
                    })
                );
            }
        },
        [getRecordFile, storeDispatch]
    );

    const handleNewOrder = () => {
        GoogleAnalyticsEventHandler({ action: 'open_create_form', category: 'Grid', label: gridName });

        // Determine view type based on defaultCreation
        const viewType = defaultCreation === 'DataFocusView' ? 'data-focused-view' : 'record-view';

        navigate(`../create-record?recordTypeId=${recordTypeId}&type=${viewType}`, { state: { prev: currentPathName } });
    };

    const formatHeaderName = useCallback(
        (key: string) => {
            const updatedKey = adjustHeaders(key);
            return updatedKey.includes(' ') ? updatedKey : intl.formatMessage({ id: updatedKey, defaultMessage: updatedKey });
        },
        [intl]
    );

    // Grid Data
    const headerFields: GridColDef[] = useMemo(() => {
        if (!headerList) return [];
        const widerFields = ['datetime', 'multiselect', 'multiselect-checkbox', 'dropdown', 'attachment'];
        const orderArray = [
            ...(currentState?.columns?.orderedFields || []),
            ...Object.keys(headerList)
                .filter((key) => ['standart', 'standard'].includes(headerList[key as keyof IRecordFieldsList].visibility.toLowerCase()))
                .filter(filterNotAllowedFields(fieldAccessList))
                .filter((key) => (key === 'enabled' ? isAllowedDelete : true))
                .filter((key) => key !== 'id')
                .filter((key) => !(currentState?.columns?.orderedFields || []).includes(key))
        ];
        return [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                id: 'selector',
                renderHeader: () => (
                    <CustomCheckboxSelectionHeader
                        apiRef={apiRef}
                        totalRecords={rowCountState}
                        isAllSelected={isBulkSelectionAllActive}
                        setAllRecordsSelected={setIsBulkSelectionAllActive}
                    />
                ),
                width: 100
            },
            {
                field: 'actions',
                type: 'actions',
                disableReorder: true,
                disableExport: true,
                align: 'center',
                minWidth: 118,
                renderCell: (params) => (
                    <RecordGridActionsColumn
                        params={params}
                        recordTypeId={recordTypeId}
                        onViewRecord={handleViewRecord}
                        onRecordShareURL={handleRecordShareURL}
                        onDownloadFile={handleDownloadFile}
                        canViewRecordViewer={canViewRecordViewer}
                        isAllowedDataFocusView={isAllowedDataFocusView}
                        isAllowedDynamicDataFocusView={isAllowedDynamicDataFocusView}
                        hasRecordFile={!!params.row.recordFile?.id}
                        onUpdate={(updatedRecord) => {
                            setRecordList((prev) => prev.map((el) => (el.id === updatedRecord.id ? updatedRecord : el)));
                            storeDispatch(
                                openSnackbar({
                                    open: true,
                                    type: 'success',
                                    title: 'Success',
                                    subtitle: `Update successful`
                                })
                            );
                        }}
                    />
                ),
                renderHeader: () => 'Actions'
            },
            {
                field: 'recordViewerUrl',
                headerName: 'Record Viewer URL',
                filterable: false,
                sortable: false,
                valueGetter: (params) => (canViewRecordViewer ? getRecordViewerURL(String(params.id), params.row.recordType) : 'N/A')
            },
            {
                field: 'dataFocusViewUrl',
                headerName: 'Dynamic Views URL',
                filterable: false,
                sortable: false,
                valueGetter: (params) =>
                    isAllowedDynamicDataFocusView ? getDataFocusViewURL(String(params.id), params.row.recordType) : 'N/A'
            },
            ...Object.keys(headerList)
                .filter((key) => ['standart', 'standard'].includes(headerList[key as keyof IRecordFieldsList].visibility.toLowerCase()))
                .filter(filterNotAllowedFields(fieldAccessList))
                .filter((key) => (key === 'enabled' ? isAllowedDelete : true))
                .filter((key) => key !== 'id')
                .sort((a, b) => {
                    if (orderArray.indexOf(a) > orderArray.indexOf(b)) {
                        return 1;
                    }
                    return -1;
                })
                .map(
                    (key) =>
                        ({
                            field: key,
                            numeric: ['decimal', 'float', 'number'].includes(headerList[key as keyof IRecordFieldsList].dataType),
                            headerName: formatHeaderName(key),
                            headerClassName: `${capitalize(key)} ${
                                !isSuperRecordType && (getEditableField(key, fieldAccessList) || editableFields[key])
                                    ? 'bills-header'
                                    : 'bills-header-default'
                            }`,
                            align: 'left' as GridAlignment,
                            width: widerFields.includes(headerList[key as keyof IRecordFieldsList].dataType) ? 250 : 150,
                            filterable: !NON_FILTERABLE_OR_SORTABLE_FIELDS.includes(key),
                            sortable:
                                !NON_FILTERABLE_OR_SORTABLE_FIELDS.includes(key) &&
                                !NON_SORTABLE_DATA_TYPES.includes(headerList[key as keyof IRecordFieldsList].dataType),
                            editable:
                                !isSuperRecordType &&
                                !!getEditableField(key, fieldAccessList) &&
                                !NON_EDITABLE_DATA_TYPES.includes(headerList[key as keyof IRecordFieldsList].dataType),
                            valueGetter: getValueGetter(key),
                            valueSetter: getValueSetter(key),
                            valueFormatter: getValueFormatter(
                                key,
                                headerList[key as keyof IRecordFieldsList].dataType,
                                headerList[key as keyof IRecordFieldsList],
                                headerList[key as keyof IRecordFieldsList]?.listType?.id
                                    ? listValuesByListId[Number(headerList[key].listType?.id)]
                                    : undefined
                            ),
                            renderCell: getRenderCell(
                                key,
                                !!getEditableField(key, fieldAccessList),
                                handleClickObject,
                                headerList[key as keyof IRecordFieldsList],
                                headerList[key as keyof IRecordFieldsList]?.listType?.id
                                    ? listValuesByListId[Number(headerList[key].listType?.id)]
                                    : undefined,
                                fieldAccessHeaders
                            ),
                            filterOperators: getFilterOperator(key, recordTypeId, headerList[key as keyof IRecordFieldsList]),
                            renderEditCell: getRenderEditCell(
                                key,
                                recordTypeId,
                                headerList[key as keyof IRecordFieldsList],
                                headerList[key as keyof IRecordFieldsList]?.listType?.id
                                    ? listValuesByListId[Number(headerList[key].listType?.id)]
                                    : undefined
                            ),
                            renderHeader: (params) => {
                                const isPinned = apiRef.current.isColumnPinned(params.colDef.field);

                                return (
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Box>{formatHeaderName(key)}</Box>
                                        {isPinned === 'left' || isPinned === 'right' ? (
                                            <Box style={{ transform: 'rotate(15deg)', marginTop: '10px' }}>
                                                <PushPinIcon fontSize="small" sx={{ color: '#858585' }} />
                                            </Box>
                                        ) : null}
                                    </Box>
                                );
                            }
                        } as GridColDef)
                )
        ];
    }, [
        headerList,
        currentState?.columns?.orderedFields,
        fieldAccessList,
        isAllowedDelete,
        apiRef,
        rowCountState,
        isBulkSelectionAllActive,
        recordTypeId,
        handleViewRecord,
        handleRecordShareURL,
        handleDownloadFile,
        canViewRecordViewer,
        isAllowedDataFocusView,
        isAllowedDynamicDataFocusView,
        storeDispatch,
        formatHeaderName,
        isSuperRecordType,
        listValuesByListId,
        handleClickObject,
        fieldAccessHeaders
    ]);

    const tableList = useMemo(
        () =>
            !headerList
                ? []
                : recordList
                      .map((row: any) =>
                          row.additionalFields.length === 0 ? row : { ...row, ...getAditionalFields(row.additionalFields, headerList) }
                      )
                      .filter((x: Record<string, string>) =>
                          Object.values(x).some((value: string) => value?.toLowerCase?.().includes(inputSearch.trim().toLowerCase()))
                      ),
        [recordList, headerList, inputSearch]
    );

    // Edit handlers
    const ToggleEdit = () => {
        const isEditing = checkIsEditing(cellModesModel);

        if (isEditing) {
            storeDispatch(cleanOpenedAction());

            const newViewModel = generateViewModel(cellModesModel);
            setCellModesModel(newViewModel);
        } else {
            storeDispatch(activeAction('record_grid'));
            const newViewModel = generateEditModel(headerFields, checkbox);
            setCellModesModel(newViewModel);
        }
    };

    const handleEditRowsModelChange = (model: GridCellModesModel) => {
        const allObjs = Object.values(model);
        const hasAnEdit = allObjs.some((el) => {
            const allEditModels = Object.values(el);

            return allEditModels.some((model) => model.mode === 'edit');
        });

        if (hasAnEdit) storeDispatch(activeAction('record_grid'));
        setCellModesModel(model);
    };

    const [bulkUpdateData, setBulkUpdateData] = useState<{ value: number; total: number; results: boolean[] }>({
        value: 0,
        total: 0,
        results: []
    });

    const bulkUpdatePercentage = useMemo(() => (bulkUpdateData.value * 100) / bulkUpdateData.total || -1, [bulkUpdateData]);

    const onSaveEdition = async () => {
        unsavedGridStateRef.current = apiRef.current.exportState();
        modal.hideConfirmation();
        const clearedObjList = findCellsEdited(apiRef.current, cellModesModel);
        // Headers
        const baseFieldHeaders = getBaseFields(headerList || null, fieldAccessList);
        const additionalFieldHeaders = getAditionalFieldsHeaders(headerList, fieldAccessList) || null;

        const updateRequestList = clearedObjList.map((record) => {
            const selectedRecord = recordList.find((el) => Number(el.id) === Number(record.id)) as IRecordHeaders;

            const { additionalFields, baseData } = getDataToUpdate(
                record,
                baseFieldHeaders,
                additionalFieldHeaders,
                selectedRecord?.additionalFields || [],
                fieldAccessList
            );

            const data: any = {
                ...baseData,
                id: Number(baseData.id)
            };

            if (additionalFields?.length) data.additionalFields = additionalFields;

            return data;
        });

        const localBulkData = { ...bulkUpdateData, total: updateRequestList.length };
        setBulkUpdateData({ ...bulkUpdateData, total: updateRequestList.length });

        try {
            for await (const request of updateRequestList) {
                const res = await new Promise<boolean>((res) => {
                    updateRecordHeader({ variables: { data: request } })
                        .then(({ data }) => {
                            setRecordList((prev) =>
                                prev.map((el) => (el.id === data?.updateRecordHeader.id ? data?.updateRecordHeader : el))
                            );
                            res(true);
                        })
                        .catch(() => res(false));
                });
                localBulkData.results = [...localBulkData.results, res];
                if (res) {
                    localBulkData.value += 1;
                    setBulkUpdateData((prev) => ({ ...prev, value: prev.value + 1, results: [...prev.results, res] }));
                } else {
                    setBulkUpdateData((prev) => ({ ...prev, results: [...prev.results, res] }));
                }
            }
            setBulkUpdateData({ total: 0, value: 0, results: [] });

            const successLength = localBulkData.results.filter((el) => el).length;
            const someKeyMatches = validateIfEditedColumnIsFiltered(clearedObjList, newFilterModel || { items: [] });

            if (successLength === updateRequestList.length && someKeyMatches) {
                newFilterModel && refetchFilteredData(newFilterModel);
            }

            ToggleEdit();

            storeDispatch(
                openSnackbar({
                    open: true,
                    type: successLength === localBulkData.total ? 'success' : 'warning',
                    title: successLength === localBulkData.total ? 'Success' : 'Partial Success',
                    subtitle: `${successLength} of ${localBulkData.total} Changes saved!`
                })
            );
        } catch (err: any) {
            console.log(err);
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Save changes failed',
                    subtitle: err.message || 'Internal server error.'
                })
            );
        }
    };

    const validateIfEditedColumnIsFiltered = (clearedObjList: Record<string, any>[], newFilterModel: CustomGridFilterModel) => {
        const fieldKeys = new Set(clearedObjList.flatMap((obj) => Object.keys(obj).filter((key) => key !== 'id')));
        const columnFilteredFields = new Set(newFilterModel?.items.map((f) => f.field));
        const someColumnsMatches = [...fieldKeys].some((key) => columnFilteredFields.has(key));
        return someColumnsMatches;
    };

    const handleSaveBulkEditionSaveConfirmation = async (attribute: string, Value: any) => {
        setBulkEditDialogOpen(false);
        await modal.showConfirmation({
            title: 'Keep Changes?',
            content: `Are you sure you want to keep these changes?`,
            loadingText: 'Updating',
            forwardButtonText: 'Yes, Confirm',
            actionButtonText: 'No, Cancel',
            onForward: async () => handleSaveBulkEdition(attribute, Value),
            onAction: null,
            showCheckbox: true,
            actionType: 'bulk_edit',
            sectionTitle: 'record_grid',
            recordType: String(gridName.toLowerCase().replaceAll(' ', '_'))
        });
    };

    const handleSaveBulkEdition = async (attribute: string, Value: any) => {
        setIsLoadingPage(true);
        const idsToUpdate = Object.values(Object.fromEntries(apiRef.current.getSelectedRows().entries())).map((el) => el.id);
        const clearedObjList = idsToUpdate.map((id) => {
            const object: { [key: string]: any } = { id };
            object[attribute] = Value;
            return object;
        });

        // Headers
        const baseFieldHeaders = getBaseFields(headerList || null, fieldAccessList);
        const aditionalFieldHeaders = getAditionalFieldsHeaders(headerList, fieldAccessList) || null;

        const selectedRecord = recordList.find((el) => Number(el.id) === Number(idsToUpdate[0])) as IRecordHeaders;

        const { additionalFields, baseData } = getDataToUpdate(
            clearedObjList[0],
            baseFieldHeaders,
            aditionalFieldHeaders,
            selectedRecord?.additionalFields || [],
            fieldAccessList
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...baseDataWithoutId } = baseData;

        const fieldToUpdate: any = {
            ...baseDataWithoutId
        };

        if (additionalFields?.length) fieldToUpdate.additionalFields = additionalFields;

        const updateRequest = { ...fieldToUpdate, tenant: Number(localStorage.getItem('tenant_id')) };

        try {
            await new Promise<boolean>((res) => {
                updateBulkRecordHeader({ variables: { ids: idsToUpdate.map((id) => Number(id)), data: updateRequest } })
                    .then(({ data }) => {
                        const { updateBulkRecordHeader } = data || { updateBulkRecordHeader: [] };
                        updateBulkRecordHeader.forEach((updated) => {
                            setRecordList((prev) => prev.map((el) => (el.id === updated.id ? updated : el)));
                        });
                        res(true);
                    })
                    .catch(() => res(false));
            });

            apiRef.current.setRowSelectionModel([]);
            handleGridRefresh();
            setIsLoadingPage(false);
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: `Changes saved!`,
                    subtitle: `${idsToUpdate.length} Records updated`
                })
            );
        } catch (err: any) {
            console.log(err);
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Save changes failed',
                    subtitle: err.message || 'Internal server error.'
                })
            );
        }
    };

    const handleDelete = async () => {
        const successdeletedlist: number[] = [];
        const updateRequestList = checkbox.map((recordId) => ({ id: Number(recordId), enabled: false }));

        try {
            for (const data of updateRequestList) {
                // eslint-disable-next-line no-await-in-loop
                const res = await new Promise<boolean>((res) => {
                    updateRecordHeader({ variables: { data } })
                        .then(() => res(true))
                        .catch(() => res(false));
                });
                if (res) successdeletedlist.push(data.id);
            }

            if (successdeletedlist.length === updateRequestList.length) {
                apiRef.current.setRowSelectionModel([]);
                setCheckbox([]);
            }

            setRecordList((prev) => prev.filter((el) => !successdeletedlist.includes(Number(el.id))));
            setRowCountState((prev) => prev - successdeletedlist.length);

            storeDispatch(
                openSnackbar({
                    open: true,
                    type: successdeletedlist.length === updateRequestList.length ? 'success' : 'warning',
                    title: successdeletedlist.length === updateRequestList.length ? 'Success' : 'Partial Success',
                    subtitle: `${successdeletedlist.length} of ${updateRequestList.length} records deleted!`
                })
            );
        } catch (err: any) {
            console.log(err);
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Delete records failed',
                    subtitle: err.message || 'Internal server error.'
                })
            );
        }
    };

    // Change Grid handlers
    const onStateChange = (state: GridState) => {
        const actualState = apiRef.current.exportState();
        densityFactorRef.current = state?.density.factor ?? 1;
        const orderedFieldsCopy = _.cloneDeep(currentState?.columns?.orderedFields);
        if (orderedFieldsCopy?.includes('__reorder__')) orderedFieldsCopy?.shift();
        if (
            _.isEqual(currentState?.pagination, actualState?.pagination) &&
            _.isEqual(currentState?.filter, actualState?.filter) &&
            _.isEqual(currentState?.pinnedColumns, actualState?.pinnedColumns) &&
            _.isEqual(currentState?.preferencePanel, actualState?.preferencePanel) &&
            _.isEqual(currentState?.columns?.columnVisibilityModel, actualState?.columns?.columnVisibilityModel) &&
            _.isEqual(orderedFieldsCopy, actualState?.columns?.orderedFields) &&
            _.isEqual(currentState?.sorting, actualState?.sorting) &&
            densityFactorRef.current === Number(localStorage.getItem('grid_density_factor'))
        ) {
            setShowSaveChanges(false);
        } else {
            setShowSaveChanges(true);
        }
    };

    const onPreferenceSave = () => {
        const actualState = apiRef.current.exportState();
        setCurrentState(actualState);
        setShowSaveChanges(false);
    };

    const handleDetailPanelExpandedRowIdsChange = useCallback(
        (newIds: GridRowId[]) => {
            // debugger;
            if (!isEditingLineItem) {
                if (newIds.length) setDetailPanelExpandedRowIds([newIds[newIds.length - 1]]);
                else setDetailPanelExpandedRowIds([]);
                return;
            }

            setNewRowId(newIds[newIds.length - 1] as number);
            setShowCloseDetailsPanel(true);
        },
        [isEditingLineItem]
    );

    const handleDetailExpanedPanelConfirm = () => {
        setDetailPanelExpandedRowIds([newRowId as number]);
        setShowCloseDetailsPanel(false);
        setIsEditingLineItem(false);
    };

    const handleChangeDefaultGrid = (newDefault: IGridPreference | null, applyQueryParams?: boolean) => {
        setShowSaveChanges(false);
        if (!headerList || loadingLineItems) return;
        if (newDefault) {
            hasLoadedInitialStateRef.current = false;
            setDefaultGrid(newDefault);
            const state = generateGridState(
                newDefault,
                headerList,
                !!lineItemObj && isAllowedLineItemAccess,
                listValuesByListId,
                applyQueryParams ? (queryParams as Record<string, string>) : undefined,
                fieldAccessList
            );
            const initialFilterModel: CustomGridFilterModel = state.filter?.filterModel || { items: [] };
            const viewDefaultInitialFilterModel: CustomGridFilterModel = {
                ...initialFilterModel,
                items: initialFilterModel.items.filter((item) => !item.isNotAllowed)
            };
            const initialSortModel = state.sorting?.sortModel || [{ field: 'createdAt', sort: 'desc' }];
            const initialPageSize = newDefault.gridOptions[0].pageSize || 25;

            defaultFilterModelRef.current = initialFilterModel;
            viewFilterModelRef.current = viewDefaultInitialFilterModel;
            setInitialState({
                loading: false,
                state
            });
            setCurrentPageSize(newDefault.gridOptions[0].pageSize || 25);
            setCurrentState(state);
            apiRef.current.setFilterModel(viewDefaultInitialFilterModel);

            setNewFilterModel(viewDefaultInitialFilterModel);
            setAppliedFilterModel(viewDefaultInitialFilterModel);

            if (!hasLoadedInitialStateRef.current) {
                hasLoadedInitialStateRef.current = true;
                handleSubmitAndSortFilters(initialSortModel, initialFilterModel, getRecords, initialPageSize);
            }
            // restore is important to reflect the newly selected grid preference changes
            if (apiRef.current.restoreState) {
                apiRef.current.restoreState(state);
            }
        } else {
            setInitialState({ loading: false });
        }
    };
    // Delete filters
    const handleResetRowFilters = async () => {
        const currentModel = defaultGrid?.role ? viewFilterModelRef.current : emptyFilterModel;
        apiRef.current.setFilterModel(currentModel);
        const sortModel = gridSortModelSelector(apiRef);
        setAppliedFilterModel(currentModel);
        await handleSubmitAndSortFilters(sortModel, currentModel);
    };

    const handleResetRowOrder = async () => {
        const revertedSort = [{ field: 'createdAt', sort: 'desc' as GridSortDirection }];
        apiRef.current.setSortModel(revertedSort);
        const filterModel = gridFilterModelSelector(apiRef);
        setShowResetRowOrder(false);
        await handleSubmitAndSortFilters(revertedSort, filterModel);
    };
    // Refresh
    const handleGridRefresh = async (initialVariables?: FindRecordHeaderWithFilteringVariables) => {
        setIsLoadingPage(true);
        const variables = initialVariables || {
            data: {
                recordTypeId,
                tenantId: Number(localStorage.getItem('tenant_id')),
                enabled: showOnlyEnabledRecords || undefined,
                filters
            },
            pagination: {
                offset: currentPage * currentPageSize,
                limit: currentPageSize
            }
        };

        const { data } = await getRecords({ variables });
        if (data) {
            setRowCountState((prev) => data.filterAndSortRecordHeader.total ?? prev);
            setRecordList(data.filterAndSortRecordHeader.records);
        }
        setIsLoadingPage(false);
        setCurrentPage(currentPage);
    };

    // Pagination
    const handlePageChange = async (page: number) => {
        if (!defaultGrid || initialState.loading) return;
        setIsLoadingPage(true);
        const { data } = await getRecords({
            variables: {
                data: {
                    recordTypeId,
                    tenantId: Number(localStorage.getItem('tenant_id')),
                    enabled: showOnlyEnabledRecords || undefined,
                    filters
                },
                pagination: {
                    offset: page * currentPageSize,
                    limit: currentPageSize
                }
            }
        });
        if (data) {
            setRowCountState((prev) => data.filterAndSortRecordHeader.total ?? prev);
            setRecordList(data.filterAndSortRecordHeader.records);
        }
        setIsLoadingPage(false);
        setCurrentPage(page);
    };

    const handlePageSizeChange = async (pageSize: number) => {
        if (!defaultGrid || initialState.loading) return;
        GoogleAnalyticsEventHandler({ action: 'page_size_changed', category: 'Grid', label: gridName, value: pageSize });
        setCurrentPage(0);
        setIsLoadingPage(true);
        setCurrentPageSize(pageSize);
        const { data } = await getRecords({
            variables: {
                data: {
                    tenantId: Number(localStorage.getItem('tenant_id')),
                    enabled: showOnlyEnabledRecords || undefined,
                    recordTypeId,
                    filters
                },
                pagination: {
                    offset: 0,
                    limit: pageSize
                }
            }
        });
        if (data) {
            setRowCountState((prev) => data.filterAndSortRecordHeader.total ?? prev);
            setRecordList(data.filterAndSortRecordHeader.records);
        }
        setIsLoadingPage(false);
    };

    const handleFilterChange = async (model: CustomGridFilterModel) => {
        const currentModel = defaultGrid?.role ? viewFilterModelRef.current : emptyFilterModel;
        setNewFilterModel(model);
        if (model.items.length === 0 && (appliedFilterModel?.items?.length ?? 0) > 0) {
            setAppliedFilterModel(currentModel);
            apiRef.current.setFilterModel(currentModel);
            const sortModel = gridSortModelSelector(apiRef);
            await handleSubmitAndSortFilters(sortModel, currentModel);
        }
    };

    const refetchFilteredData = async (newFilterModel: CustomGridFilterModel) => {
        const sortModel = gridSortModelSelector(apiRef);
        await handleSubmitAndSortFilters(sortModel, newFilterModel);
    };

    const handleSortChange = async (model: GridSortModel) => {
        if (!model.length) return;
        setShowResetRowOrder(true);
        const filterModel = gridFilterModelSelector(apiRef);
        await handleSubmitAndSortFilters(model, filterModel);
    };

    const handleClickApply = async () => {
        const sortModel = gridSortModelSelector(apiRef);
        const filterModel = gridFilterModelSelector(apiRef);

        setAppliedFilterModel(filterModel);

        await handleSubmitAndSortFilters(sortModel, filterModel, getRecords);
    };

    const handleSubmitAndSortFilters = async (
        initialSortModel: GridSortModel,
        initialFilterModel: CustomGridFilterModel,
        getDataFn: (options: any) => Promise<any> = fetchMore,
        limit: number = currentPageSize
    ) => {
        if (initialSortModel.length >= 1) {
            if (initialSortModel[0].field === 'createdAt' && initialSortModel[0].sort === 'desc' && initialSortModel.length === 1) {
                setShowResetRowOrder(false);
            } else {
                setShowResetRowOrder(true);
            }
        } else {
            setShowResetRowOrder(false);
        }

        const backofficeFilters = defaultGrid?.role ? defaultFilterModelRef.current.items : [];

        const filterModel = {
            ...initialFilterModel,
            items: [...initialFilterModel.items, ...backofficeFilters]
        };
        const sortModel = initialSortModel;
        // By Default sort by created date if theres no sort model
        if (sortModel.length === 0) sortModel.push({ field: 'createdAt', sort: 'desc' });

        const filters = generateFilterPayloads(filterModel, sortModel, headerList);
        setIsLoadingPage(true);
        setFilters(filters);
        const { data } = await getDataFn({
            variables: {
                data: {
                    tenantId: Number(localStorage.getItem('tenant_id')),
                    enabled: showOnlyEnabledRecords || undefined,
                    recordTypeId,
                    filters
                },
                pagination: {
                    offset: 0,
                    limit
                }
            }
        });
        setRowCountState((prev) => data?.filterAndSortRecordHeader.total ?? prev);
        setRecordList(data?.filterAndSortRecordHeader.records || []);
        setIsLoadingPage(false);
    };

    const handleResetFilters = ({ openedPanelValue }: GridPreferencePanelParams) => {
        if (openedPanelValue === GridPreferencePanelsValue.filters && appliedFilterModel && apiRef.current) {
            const filterModel = gridFilterModelSelector(apiRef);
            const sortModel = gridSortModelSelector(apiRef);
            if (filterModel.items.length === 0) handleSubmitAndSortFilters(sortModel, filterModel);
            else apiRef.current.setFilterModel(appliedFilterModel);
        }
    };

    // Edit dialog
    const handleShowEditDialog = async () => {
        await modal.showConfirmation({
            title: 'Keep Changes?',
            content: `Are you sure you want to keep these changes?`,
            loadingText: 'Updating',
            forwardButtonText: 'Yes, Confirm',
            actionButtonText: 'No, Cancel',
            onForward: onSaveEdition,
            onAction: null,
            showCheckbox: true,
            actionType: 'edit',
            sectionTitle: 'record_grid',
            recordType: String(gridName.toLowerCase().replaceAll(' ', '_'))
        });
    };

    const handleClickBulkEdit = () => {
        if (!headerList) return;

        setColumnsEnabledToBulkEdit(
            Object.keys(headerList)
                .filter((key) => ['standart', 'standard'].includes(headerList[key as keyof IRecordFieldsList].visibility.toLowerCase()))
                .filter(filterNotAllowedFields(fieldAccessList))
                .filter((key) => (key === 'enabled' ? isAllowedDelete : true))
                .filter((key) => key !== 'id')
                .map((key) => ({
                    key,
                    editable: !!getEditableField(key, fieldAccessList)
                }))
                .filter((el) => el.editable)
                .map((el) => el.key)
        );
    };

    const PanelTitle = (rightPanelWidth: number) => (
        <PanelTitleWithShortCut
            title={selectedObjectToEdit?.field ?? ''}
            id="titleId"
            showShortCuts={isAllowedToEditObject && objectPanelMode === ObjectPanelMode.VIEW}
            shortCutAction={() => {
                if (isAllowedToEditObject) {
                    setObjectPanelMode(ObjectPanelMode.EDIT);
                } else if (isListObjectSelected) {
                    listObjectWarning();
                }
            }}
            onClickHeader={() => {}}
            rightHandPanelWidth={rightPanelWidth}
        />
    );

    const ObjectPanelContainer = (rightPanelWidth: number) => (
        <Box height="100%" width="100%" onDoubleClick={() => isAllowedToEditObject && setObjectPanelMode(ObjectPanelMode.EDIT)}>
            {objectPanelMode === ObjectPanelMode.VIEW && (
                <ObjectPanelView
                    loadingObjectProperties={loadingObjectProperties}
                    handleClosePanel={() => {
                        setSelectedObjectToEdit(null);
                        setShowObjectPanel(false);
                        setObjectProperties([]);
                        setObjectPanelMode(ObjectPanelMode.VIEW);
                    }}
                    objectProperties={objectProperties}
                    showFieldMenu={false}
                    objectEditingRestricted={!isAllowedToEditObject}
                    rightHandPanelWidth={rightPanelWidth}
                    PanelTitle={PanelTitle}
                />
            )}
            {objectPanelMode === ObjectPanelMode.EDIT && (
                <ObjectPanelEditForm
                    objectProperties={objectProperties}
                    setObjectProperties={setObjectProperties}
                    handleUpdateObjectGrid={handleEditObjectFormInteraction}
                    handleClosePanelGrid={handleEditObjectFormInteraction}
                    updateLoading={updateLoading}
                    showFieldMenu={false}
                    rightHandPanelWidth={rightPanelWidth}
                    PanelTitle={PanelTitle}
                />
            )}
        </Box>
    );

    useEffect(() => {
        if (columnsEnabledToBulkEdit.length) setBulkEditDialogOpen(true);
    }, [columnsEnabledToBulkEdit]);

    // Apollo Error Handlers
    useEffect(() => {
        if (accessError) {
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Failed to fetch fields access',
                    subtitle: accessError.message.replaceAll(':', ',')
                })
            );
        }
        if (lineItemsError) {
            storeDispatch(
                openSnackbar({
                    open: true,
                    type: 'error',
                    title: 'Failed to fetch line items',
                    subtitle: lineItemsError.message.replaceAll(':', ',')
                })
            );
        }
    }, [accessError, lineItemsError, storeDispatch]);

    useEffect(() => {
        if (!isOpenImportDialog) setObjectsToCreate({});
    }, [isOpenImportDialog]);

    useEffect(() => {
        setTimeout(() => {
            if (!unsavedGridStateRef.current || !apiRef.current) return;
            apiRef.current.restoreState(unsavedGridStateRef.current);
        }, 0);
    }, [headerFields, apiRef]);

    const listObjectWarning = () => {
        storeDispatch(
            openSnackbar({
                open: true,
                type: 'warning',
                title: `Warning`,
                subtitle: `Contact the List Administrator`
            })
        );
    };

    // This was used for a legacy feature that check last cell editing for used edit in bulk
    // useEffect(() => {
    //     Object.keys(cellModesModel).some((key) => {
    //         const column =
    //             Object.keys(cellModesModel[key]).find((columns) => Object.keys(cellModesModel[key][columns]).includes('value')) || '';
    //         if (column) setEditingId(key);
    //         setEditableField({ [column]: cellModesModel[key][column] });
    //         return column;
    //     });
    // }, [cellModesModel]);

    // const { loading: userPreferencesLoading, data: userPreferencesData } = useQuery<ISaasMyGridPreference>(PREFERENCES_BACKOFFICE, {
    //     variables: { gridName: gridName || 'bills' },
    //     onCompleted(data) {
    //         if (!data || !data.SaasMyGridPreferences?.length) {
    //             handleChangeDefaultGrid(null);
    //         } else {
    //             const defaultGrid = getDefaultGrid(preferenceList);
    //             setPreSelectedGridId(+defaultGrid.id);
    //             handleChangeDefaultGrid(defaultGrid);
    //         }
    //     }
    // });

    // const preferenceList = useMemo(() => userPreferencesData?.SaasMyGridPreferences || [], [userPreferencesData]);

    const isLoading = headerLoading || loadingPreferences || accessLoading || initialState.loading;

    return (
        <RightHandResizerContainer
            activateResize={showObjectPanel}
            drawerOpen={drawerOpen}
            initialWidth={showObjectPanel ? -1 : 0}
            onCenterWidthChange={(width) => {
                setRightPanelAndDrawerWidth(width.rightPanelAndDrawerWidth);
                setRightPanelWidth(width.rightPanelWidth);
            }}
            centerContent={
                <>
                    <Grid container width={`calc(100vw - ${rightPanelAndDrawerWidth}px)`} height="100%">
                        <GridOptions
                            onPreferenceSave={onPreferenceSave}
                            loadPreferences={
                                !headerLoading && !loadingLineItems && !accessLoading && !loadingRecordTypes && !loadingMyMenuItems
                            }
                            disabled={loadingSelectedCustomListValues || listLoading}
                            showSaveChanges={showSaveChanges}
                            setDefaultGrid={handleChangeDefaultGrid}
                            onLoading={setLoadingPreferences}
                            inputValue={inputSearch}
                            onChangeInputValue={setInputSearch}
                            gridName={gridName}
                            densityFactor={densityFactorRef}
                            apiRef={apiRef}
                            recordTypeId={recordTypeId}
                            createOrder={handleNewOrder}
                            isSuperRecordType={isSuperRecordType}
                            canCreateNewRecord={canCreateNewRecord || false}
                        />
                        {isLoading && <SkeletonGrid />}

                        {!!headerFields.length && !isLoading && (
                            <PinnedActionsProvider recordTypeName={gridName} recordTypeId={recordTypeId}>
                                <Box
                                    component="div"
                                    id="data-grid-container"
                                    style={{
                                        width: `calc(100vw - ${rightPanelAndDrawerWidth}px)`,
                                        height: '84vh',
                                        opacity: accessLoading ? '0' : '1',
                                        overflowY: 'auto'
                                    }}
                                >
                                    <DataGridPro
                                        showColumnVerticalBorder
                                        showCellVerticalBorder
                                        paginationMode="server"
                                        filterMode="server"
                                        sortingMode="server"
                                        getCellClassName={(params) => {
                                            let className = '';
                                            const col: any = headerFields.find((c) => c.field === params.field);
                                            if (col && !getEditableField(col?.field || '', fieldAccessList, col?.id || '')) {
                                                className = 'non-editable-cell';
                                            }

                                            return className;
                                        }}
                                        sx={(theme) => ({
                                            '& .non-editable-cell': {
                                                backgroundColor: theme.palette.primary[500]
                                            },
                                            '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': {
                                                boxShadow: 'none'
                                            },
                                            '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                                                '& svg[data-testid="CheckBoxIcon"]': {
                                                    color: `${theme.palette.primary.dark} !important`,
                                                    backgroundColor: 'transparent !important'
                                                }
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                borderBottom: '2.5px solid #E0E0E0'
                                            },
                                            '& .MuiDataGrid-withBorderColor': {
                                                borderRight: '1px solid #E0E0E0',
                                                borderBottom: '1px solid #E0E0E0'
                                            },
                                            '& .MuiDataGrid-columnHeader': {
                                                borderRight: '1px solid transparent'
                                            },
                                            '& .MuiDataGrid-cellCheckbox > svg[data-testid="CheckBoxIcon"]': {
                                                color: `${theme.palette.primary.dark} !important`
                                            },
                                            '& .Mui-selected': {
                                                backgroundColor: `${theme.palette.primary.dark} !important`,
                                                color: `${theme.palette.background.paper} !important`,
                                                '& div button, div a, div p': { color: theme.palette.background.paper },
                                                '& .non-editable-cell': {
                                                    color: `${theme.palette.primary[600]} !important`,
                                                    '& .gridObjectValue': {
                                                        color: `${theme.palette.primary.dark} !important`
                                                    }
                                                },
                                                '& div button': { color: `${theme.palette.background.paper} !important` },
                                                '& svg[data-testid="CheckBoxIcon"], svg[data-testid="CheckBoxOutlineBlankIcon"]': {
                                                    color: `${theme.palette.background.paper} !important`,
                                                    backgroundColor: 'transparent !important'
                                                }
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                border: 'none !important',
                                                '& .MuiDataGrid-selectedRowCount': {
                                                    display: 'none !important'
                                                }
                                            },
                                            '& .MuiDataGrid-columnHeader--filtered .MuiDataGrid-iconButtonContainer': {
                                                display: (appliedFilterModel?.items ?? []).length > 0 ? 'block' : 'none'
                                            }
                                        })}
                                        apiRef={apiRef}
                                        slots={{
                                            toolbar: CustomToolbar,
                                            filterPanel: FilterPanel,
                                            pagination: PaginationFooter,
                                            columnMenu: CustomColumnMenu
                                        }}
                                        onPreferencePanelClose={handleResetFilters}
                                        density={getDensityFactor(densityFactorRef.current)}
                                        pageSizeOptions={[25, 50, 100, 200, 500, 1000, 2000, 3000]}
                                        sortingOrder={['desc', 'asc']}
                                        onRowSelectionModelChange={(model) => {
                                            setIsBulkSelectionAllActive(false);
                                            setCheckbox(model as string[]);
                                        }}
                                        columns={headerFields}
                                        rows={tableList || []}
                                        initialState={initialState.state}
                                        cellModesModel={cellModesModel}
                                        onStateChange={onStateChange}
                                        onCellEditStop={handleRowEditStop}
                                        onCellEditStart={(_params, event) => {
                                            if (!isAllowedEditRecords) event.defaultMuiPrevented = true;
                                        }}
                                        onCellKeyDown={handleCellKeydown}
                                        onCellModesModelChange={handleEditRowsModelChange}
                                        isRowSelectable={() => !checkIsEditing(cellModesModel)}
                                        onRowEditStop={handleRowEditStop}
                                        onRowModesModelChange={(model) => handleEditRowsModelChange(model as unknown as GridCellModesModel)}
                                        rowCount={rowCountState}
                                        paginationModel={{ page: currentPage, pageSize: currentPageSize }}
                                        onSortModelChange={handleSortChange}
                                        onFilterModelChange={handleFilterChange}
                                        getDetailPanelContent={
                                            lineItemObj && isAllowedLineItemAccess
                                                ? ({ row }: GridRowParams<IRecordHeaders>) => (
                                                      <CustomDetailPanel
                                                          headers={lineItemObj}
                                                          isOpen={detailPanelExpandedRowIds.includes(row.id)}
                                                          recordId={Number(row.id)}
                                                          key={row.id}
                                                          recordTypeId={recordTypeId}
                                                          onActiveEditing={setIsEditingLineItem}
                                                      />
                                                  )
                                                : undefined
                                        }
                                        getDetailPanelHeight={() => 460}
                                        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                                        onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                                        checkboxSelection
                                        pagination
                                        rowReordering={false}
                                        disableRowSelectionOnClick
                                        getRowClassName={getRowCustomClassName(classes)}
                                        slotProps={{
                                            filterPanel: {
                                                isApplied: isSameFilterModel,
                                                isLoading: isLoadingPage,
                                                onClickApply: handleClickApply,
                                                disableApply: !checkFilterValue(newFilterModel || ({} as CustomGridFilterModel)),
                                                filterModel: apiRef?.current?.state?.filter ? gridFilterModelSelector(apiRef) : undefined
                                            } as FilterPanelProps,
                                            pagination: {
                                                classes,
                                                rowCountState,
                                                currentPage,
                                                handlePageChange,
                                                currentPageSize,
                                                handlePageSizeChange,
                                                customSelectionCounter: true,
                                                isAllSelected: isBulkSelectionAllActive
                                            } as PaginationFooterProps,
                                            columnsPanel: {
                                                getTogglableColumns
                                            },
                                            columnMenu: {
                                                fieldHeaders: headerList,
                                                onApplyFilters: handleClickApply
                                            } as Partial<CustomColumnMenuProps>,
                                            toolbar: {
                                                getZipFileUrl,
                                                recordType: String(gridName.toLowerCase().replaceAll(' ', '_')),
                                                rowsSelectedAmount: isBulkSelectionAllActive ? rowCountState : 0,
                                                handleGridRefresh,
                                                handleResetRowFilters,
                                                showResetRowOrder,
                                                handleResetRowOrder,
                                                showOnlyEnabledRecords,
                                                onClickEdit: ToggleEdit,
                                                rowsEditModel: cellModesModel,
                                                onSave: handleShowEditDialog,
                                                onDelete: handleDelete,
                                                onToggleShowRecords: () => {
                                                    const newVal = !showOnlyEnabledRecords;
                                                    setShowOnlyEnabledRecords(newVal);
                                                    handleGridRefresh({
                                                        data: {
                                                            recordTypeId,
                                                            tenantId: Number(localStorage.getItem('tenant_id')),
                                                            enabled: newVal || undefined,
                                                            filters
                                                        },
                                                        pagination: {
                                                            offset: 0,
                                                            limit: currentPageSize
                                                        }
                                                    });
                                                },
                                                onClickBulkEdit: handleClickBulkEdit,
                                                updateLoading,
                                                isSuperRecordType,
                                                appliedFilterModel,
                                                recordTypeId,
                                                showObjectPanel: showObjectPanel ?? false,
                                                ObjectPanelContainer: () => ObjectPanelContainer(rightPanelWidth) ?? null,
                                                isOpenImportDialog: isOpenImportDialog ?? false,
                                                setIsOpenImportDialog: setIsOpenImportDialog ?? (() => {}),
                                                objectsToCreate: objectsToCreate ?? []
                                            } as ICustomToolbar
                                        }}
                                    />
                                </Box>
                            </PinnedActionsProvider>
                        )}
                    </Grid>
                    <UpdateRowsBanner percentage={bulkUpdatePercentage} value={bulkUpdateData.value} total={bulkUpdateData.total} />
                    <CloseDetailsPanelDialog
                        open={showCloseDetailsPanel}
                        onClose={() => setShowCloseDetailsPanel(false)}
                        onSubmit={handleDetailExpanedPanelConfirm}
                    />
                    <BulkEditDialog
                        formatHeaderName={formatHeaderName}
                        setOpen={setBulkEditDialogOpen}
                        open={bulkEditDialogOpen}
                        recordTypeId={recordTypeId}
                        recordTypeName={gridName}
                        columns={columnsEnabledToBulkEdit}
                        fields={headerList}
                        onSaveBulkEdition={handleSaveBulkEditionSaveConfirmation}
                    />
                </>
            }
            rightContent={ObjectPanelContainer(rightPanelWidth)}
        />
    );
};
